/**
 * Tabela CRUD de planos do catálogo de billing.
 *
 * Operações: listar, criar (provisiona Product+Price no Stripe), editar
 * (nome/créditos/ativo/padrão via PUT), mudar preço (novo Price; assinantes
 * atuais mantêm o anterior — grandfathering) e deletar.
 * Campos: key, name, stripe_price_id, stripe_product_id, amount_brl_cents,
 * monthly_credits, sort_order, is_active, is_default.
 */
import { Fragment, useState } from 'react';
import { Button } from '../../design-system/components/Button';
import { Card } from '../../design-system/components/Card';
import { TextField } from '../../design-system/components/TextField';
import { Spinner } from '../../design-system/components/Spinner';
import { ErrorState } from '../../shared/components/ErrorState';
import { EmptyState } from '../../shared/components/EmptyState';
import {
  useChangePlanPrice,
  useDeletePlan,
  useProvisionPlan,
  usePlans,
  useUpdatePlan,
} from './hooks';
import type { PlanProvision, PlanPublic, PlanUpdate } from './types';

/** Formata centavos de BRL como moeda pt-BR, ex: "R$ 99,90". */
function formatAmountBrl(amountBrlCents: number | null | undefined): string {
  if (amountBrlCents === null || amountBrlCents === undefined) return '—';
  return (amountBrlCents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

interface PlanFormState {
  key: string;
  name: string;
  amount_brl: string;
  monthly_credits: string;
  sort_order: string;
  is_active: boolean;
  is_default: boolean;
}

function emptyFormState(): PlanFormState {
  return {
    key: '',
    name: '',
    amount_brl: '0',
    monthly_credits: '0',
    sort_order: '0',
    is_active: true,
    is_default: false,
  };
}

function planToFormState(plan: PlanPublic): PlanFormState {
  return {
    key: plan.key,
    name: plan.name,
    amount_brl:
      plan.amount_brl_cents !== null && plan.amount_brl_cents !== undefined
        ? String(plan.amount_brl_cents / 100)
        : '0',
    monthly_credits: String(plan.monthly_credits),
    sort_order: String(plan.sort_order),
    is_active: plan.is_active,
    is_default: plan.is_default,
  };
}

interface PlanFormProps {
  initial: PlanFormState;
  onSave: (form: PlanFormState) => void;
  onCancel: () => void;
  isSaving: boolean;
  isEdit: boolean;
}

function PlanForm({ initial, onSave, onCancel, isSaving, isEdit }: PlanFormProps) {
  const [form, setForm] = useState<PlanFormState>(initial);

  function set(field: keyof PlanFormState, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <div className="billing-plan-form">
      <div className="dash-grid">
        <TextField
          label="Chave do plano"
          value={form.key}
          onChange={(e) => set('key', e.target.value)}
          disabled={isEdit}
          required
          helperText="Identificador único, ex: starter"
        />
        <TextField
          label="Nome"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          required
        />
        {isEdit ? null : (
          <TextField
            label="Valor (R$/mês)"
            type="number"
            step="0.01"
            min="0"
            value={form.amount_brl}
            onChange={(e) => set('amount_brl', e.target.value)}
            required
            helperText="Preço mensal em reais, ex: 99.90"
          />
        )}
        <TextField
          label="Créditos mensais"
          type="number"
          step="1"
          min="0"
          value={form.monthly_credits}
          onChange={(e) => set('monthly_credits', e.target.value)}
          required
        />
        <TextField
          label="Ordem de exibição"
          type="number"
          step="1"
          min="0"
          value={form.sort_order}
          onChange={(e) => set('sort_order', e.target.value)}
        />
      </div>
      <div className="row row--sm-gap billing-admin__row-mt-md">
        <label className="row row--sm-gap">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
          />
          <span>Ativo</span>
        </label>
        <label className="row row--sm-gap">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => set('is_default', e.target.checked)}
          />
          <span>Padrão</span>
        </label>
      </div>
      <div className="row row--sm-gap billing-admin__row-mt-md">
        <Button onClick={() => onSave(form)} isLoading={isSaving} disabled={isSaving}>
          {isEdit ? 'Salvar alterações' : 'Criar plano'}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

export function PlansTable() {
  const query = usePlans();
  const provisionMutation = useProvisionPlan();
  const updateMutation = useUpdatePlan();
  const deleteMutation = useDeletePlan();
  const changePriceMutation = useChangePlanPrice();

  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editingPlan, setEditingPlan] = useState<PlanPublic | null>(null);
  const [formError, setFormError] = useState<unknown>(null);
  const [formValidacao, setFormValidacao] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<unknown>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [changingPriceKey, setChangingPriceKey] = useState<string | null>(null);
  const [newPriceBrl, setNewPriceBrl] = useState('');
  const [priceError, setPriceError] = useState<unknown>(null);
  const [priceValidacao, setPriceValidacao] = useState<string | null>(null);
  const [priceChangeFeedback, setPriceChangeFeedback] = useState<{
    key: string;
    changed: boolean;
  } | null>(null);

  function handleCreate(form: PlanFormState) {
    setFormError(null);
    setFormValidacao(null);
    const amountBrlCents = Math.round(parseFloat(form.amount_brl) * 100);
    const monthlyCredits = parseInt(form.monthly_credits, 10);
    if (form.key.trim() === '' || form.name.trim() === '') {
      setFormValidacao('Preencha a chave e o nome do plano.');
      return;
    }
    if (!(amountBrlCents > 0)) {
      setFormValidacao('Informe um valor mensal válido, maior que zero.');
      return;
    }
    if (!(monthlyCredits > 0)) {
      setFormValidacao('Informe uma quantidade de créditos mensais maior que zero.');
      return;
    }
    const sortOrder = parseInt(form.sort_order, 10);
    const payload: PlanProvision = {
      key: form.key.trim(),
      name: form.name.trim(),
      amount_brl_cents: amountBrlCents,
      monthly_credits: monthlyCredits,
      sort_order: Number.isNaN(sortOrder) ? 0 : sortOrder,
      is_default: form.is_default,
    };
    provisionMutation.mutate(payload, {
      onSuccess: () => setMode('list'),
      onError: (err) => setFormError(err),
    });
  }

  function handleUpdate(form: PlanFormState) {
    if (editingPlan === null) return;
    setFormError(null);
    setFormValidacao(null);
    const monthlyCredits = parseInt(form.monthly_credits, 10);
    if (form.name.trim() === '') {
      setFormValidacao('Preencha o nome do plano.');
      return;
    }
    if (!(monthlyCredits > 0)) {
      setFormValidacao('Informe uma quantidade de créditos mensais maior que zero.');
      return;
    }
    const sortOrder = parseInt(form.sort_order, 10);
    const payload: PlanUpdate = {
      name: form.name.trim(),
      monthly_credits: monthlyCredits,
      sort_order: Number.isNaN(sortOrder) ? 0 : sortOrder,
      is_active: form.is_active,
      is_default: form.is_default,
    };
    updateMutation.mutate(
      { key: editingPlan.key, payload },
      {
        onSuccess: () => {
          setMode('list');
          setEditingPlan(null);
        },
        onError: (err) => setFormError(err),
      },
    );
  }

  function handleDelete(key: string) {
    if (!window.confirm(`Remover o plano "${key}"?`)) return;
    setDeleteError(null);
    setDeletingKey(key);
    deleteMutation.mutate(key, {
      onError: (err) => setDeleteError(err),
      onSettled: () => setDeletingKey(null),
    });
  }

  function handleStartChangePrice(plan: PlanPublic) {
    setChangingPriceKey(plan.key);
    setNewPriceBrl(
      plan.amount_brl_cents !== null && plan.amount_brl_cents !== undefined
        ? String(plan.amount_brl_cents / 100)
        : '0',
    );
    setPriceError(null);
    setPriceValidacao(null);
    setPriceChangeFeedback(null);
  }

  function handleConfirmChangePrice(key: string) {
    setPriceValidacao(null);
    const amountBrlCents = Math.round(parseFloat(newPriceBrl) * 100);
    if (Number.isNaN(amountBrlCents) || amountBrlCents <= 0) {
      setPriceValidacao('Informe um valor válido em reais, maior que zero.');
      return;
    }
    if (
      !window.confirm(
        'O novo valor vale apenas para novas assinaturas; assinantes atuais mantêm o preço anterior. Confirmar mudança de preço?',
      )
    ) {
      return;
    }
    setPriceError(null);
    changePriceMutation.mutate(
      { key, payload: { amount_brl_cents: amountBrlCents } },
      {
        onSuccess: (result) => {
          setChangingPriceKey(null);
          setPriceChangeFeedback({ key, changed: result.changed });
        },
        onError: (err) => setPriceError(err),
      },
    );
  }

  if (query.isPending) {
    return (
      <div className="center">
        <Spinner label="Carregando planos" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => query.refetch()}
        title="Não foi possível carregar os planos"
      />
    );
  }

  const plans = query.data ?? [];

  if (mode === 'create') {
    return (
      <Card>
        <h2 className="dash-section__title">Novo plano</h2>
        {formValidacao !== null && (
          <span className="label" role="alert">
            {formValidacao}
          </span>
        )}
        {formError !== null && <ErrorState error={formError} />}
        <PlanForm
          initial={emptyFormState()}
          onSave={handleCreate}
          onCancel={() => {
            setMode('list');
            setFormError(null);
            setFormValidacao(null);
          }}
          isSaving={provisionMutation.isPending}
          isEdit={false}
        />
      </Card>
    );
  }

  if (mode === 'edit' && editingPlan !== null) {
    return (
      <Card>
        <h2 className="dash-section__title">Editar plano — {editingPlan.key}</h2>
        {formValidacao !== null && (
          <span className="label" role="alert">
            {formValidacao}
          </span>
        )}
        {formError !== null && <ErrorState error={formError} />}
        <PlanForm
          initial={planToFormState(editingPlan)}
          onSave={handleUpdate}
          onCancel={() => {
            setMode('list');
            setEditingPlan(null);
            setFormError(null);
            setFormValidacao(null);
          }}
          isSaving={updateMutation.isPending}
          isEdit
        />
      </Card>
    );
  }

  return (
    <Card>
      <div className="dash-section__header">
        <h2 className="dash-section__title">Planos</h2>
        <Button onClick={() => setMode('create')}>Novo plano</Button>
      </div>
      {deleteError !== null && (
        <ErrorState error={deleteError} title="Não foi possível remover o plano" />
      )}
      {priceChangeFeedback !== null ? (
        <div
          className="ds-toast"
          data-variant={priceChangeFeedback.changed ? 'success' : 'warning'}
          role="status"
        >
          <span className="ds-toast__title">
            {priceChangeFeedback.changed
              ? 'Preço atualizado · assinantes atuais mantêm o preço anterior'
              : 'O valor informado é igual ao atual — nada foi alterado'}
          </span>
          <Button variant="ghost" onClick={() => setPriceChangeFeedback(null)}>
            Fechar
          </Button>
        </div>
      ) : null}
      {plans.length === 0 ? (
        <EmptyState
          title="Nenhum plano cadastrado"
          description="Crie o primeiro plano para começar."
          action={<Button onClick={() => setMode('create')}>Novo plano</Button>}
        />
      ) : (
        <div className="billing-admin__scroll">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Chave</th>
                <th>Nome</th>
                <th>Valor/mês</th>
                <th>Créditos/mês</th>
                <th>Ordem</th>
                <th>Ativo</th>
                <th>Padrão</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <Fragment key={plan.key}>
                  <tr>
                    <td>{plan.key}</td>
                    <td>{plan.name}</td>
                    <td>{formatAmountBrl(plan.amount_brl_cents)}</td>
                    <td>{plan.monthly_credits}</td>
                    <td>{plan.sort_order}</td>
                    <td>{plan.is_active ? 'Sim' : 'Não'}</td>
                    <td>{plan.is_default ? 'Sim' : 'Não'}</td>
                    <td>
                      <div className="row row--sm-gap">
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setEditingPlan(plan);
                            setMode('edit');
                          }}
                        >
                          Editar
                        </Button>
                        <Button variant="secondary" onClick={() => handleStartChangePrice(plan)}>
                          Mudar preço
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => handleDelete(plan.key)}
                          isLoading={deletingKey === plan.key}
                          disabled={deletingKey !== null}
                        >
                          Remover
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {changingPriceKey === plan.key ? (
                    <tr>
                      <td colSpan={8}>
                        {priceValidacao !== null && (
                          <span className="label" role="alert">
                            {priceValidacao}
                          </span>
                        )}
                        {priceError !== null && <ErrorState error={priceError} />}
                        <div className="row row--sm-gap">
                          <TextField
                            label="Novo valor (R$/mês)"
                            type="number"
                            step="0.01"
                            min="0"
                            value={newPriceBrl}
                            onChange={(e) => setNewPriceBrl(e.target.value)}
                            helperText="Vale para novas assinaturas; assinantes atuais mantêm o preço anterior."
                          />
                          <Button
                            onClick={() => handleConfirmChangePrice(plan.key)}
                            isLoading={changePriceMutation.isPending}
                            disabled={changePriceMutation.isPending}
                          >
                            Confirmar novo preço
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setChangingPriceKey(null);
                              setPriceError(null);
                              setPriceValidacao(null);
                            }}
                            disabled={changePriceMutation.isPending}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
